/**
 * preparePayload — Phase 6a.22 (modifications) + Phase 6a.23 (FX rates) +
 * Phase 6e.4.2-hotfix-2 Bug C (numeric coercion).
 *
 * Final shape-up before a ContractFormValues object is persisted to
 * localStorage as a ContractInput slot.
 *
 * Phase 6e.4.2-hotfix-2 Bug C — numeric coercion at the payload boundary:
 *
 *   `ContractFormValues` types its numeric fields as `number` post-Zod-
 *   coercion, but RHF stores raw `<input>` values as strings while the
 *   auditor types. On manually-entered contracts the form re-uses
 *   defaultValues from a fresh form (not the resolver's parsed output)
 *   so the persisted slot carries strings. Backend's `engagementInputSchema`
 *   uses strict `z.number()` on `leaseTermMonths`, `discountRateAnnual`,
 *   `payment.amount`, `commencementSpotRate`, and `historicalRates[].rate`
 *   / `paymentDateRates[].rate` — and rejects strings with
 *   "Expected number, received string". The same fix pattern was applied
 *   to `buildFxSyncPlan.ts` in Phase 6e.4.1 (the matcher version of this
 *   bug); this fixes the calculate-endpoint payload.
 *
 *   Numeric fields coerced here (backend type quoted from
 *   `ifrs16-tool/backend/src/core/schemas.ts`):
 *     • `leaseTermMonths`        — `z.number().int().min(1).max(1200)`
 *     • `discountRateAnnual`     — `z.number().gt(0).lte(500).finite()`
 *     • `payment.amount`         — `z.number().positive().finite()`
 *     • `commencementSpotRate`   — `z.number().positive(...).optional()`
 *     • `historicalRates[].rate` — `z.number().positive(...)`
 *     • `paymentDateRates[].rate`— `z.number().positive(...)`
 *
 *   Backend stays strict; the boundary is the right place for this fix.
 *
 * Three transformation families (Phase 6a.22 / 6a.23, unchanged):
 *   1. `modifications: []` → omitted; non-empty → sorted by date.
 *   2. `historicalRates: []` → omitted; non-empty → sorted by date.
 *   3. `paymentDateRates: []` → omitted; non-empty → sorted by date.
 */

import { getCurrentLocale } from "@/lib/format";
import { normalizeDecimalInput } from "@/lib/format/sanitizeFxRatePaste";
import type { ContractInput } from "@/types";

import { computeFirstPaymentDate } from "../utils/computeFirstPaymentDate";
import type { ContractFormValues } from "./contractFormSchema";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Phase 6e.4.2-hotfix-2 Bug C. Accept either a finite number or a
 * non-empty string parseable as a finite number. Throw a typed Error
 * otherwise — the caller (engagement save path) catches and surfaces it
 * as a slot validation failure rather than letting a NaN reach the wire.
 */
function toNumber(value: unknown, fieldName: string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    // Finding TR1 — slots persist the raw free-text input strings, so a
    // TR-grouped "10.000" (= ten thousand) reached `Number()` and coerced
    // to 10: a silent 1000× understatement on the wire. Normalize through
    // the same locale-aware sanitizer the schema boundary uses; non-numeric
    // junk passes through unchanged and still throws below.
    const n = Number(normalizeDecimalInput(value, getCurrentLocale()));
    if (Number.isFinite(n)) return n;
  }
  throw new Error(
    `preparePayload: invalid numeric value for "${fieldName}": ${JSON.stringify(value)}`,
  );
}

function toOptionalNumber(
  value: unknown,
  fieldName: string,
): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string" && value.trim() === "") return undefined;
  return toNumber(value, fieldName);
}

export function preparePayload(values: ContractFormValues): ContractInput {
  const {
    modifications,
    historicalRates,
    paymentDateRates,
    termination,
    indexLinked,
    indexType,
    indexResetFrequency,
    indexCapPercent,
    indexFloorPercent,
    indexResets,
    priorReportingDate,
    priorPeriodBalances,
    priorReportingTolerance,
    residualValueGuarantee,
    purchaseOptionPrice,
    purchaseOptionReasonablyCertain,
    transfersOwnership,
    assetUsefulLifeMonths,
    ...rest
  } = values;
  const payload = { ...rest } as ContractInput;

  // Bug C — top-level required numerics.
  payload.leaseTermMonths = toNumber(
    rest.leaseTermMonths,
    "leaseTermMonths",
  );
  payload.discountRateAnnual = toNumber(
    rest.discountRateAnnual,
    "discountRateAnnual",
  );

  // Bug C — payment.amount (nested).
  if (rest.payment) {
    payload.payment = {
      ...rest.payment,
      amount: toNumber(rest.payment.amount, "payment.amount"),
    };
  }

  // Phase 6e.4.5 — defense L3: backstop `payment.firstPaymentDate`.
  // Layer 1 (UI useEffect in PaymentInfoSection) populates this on
  // every form interaction; layer 2 (Excel parser) fills it during
  // bulk import; this layer catches any path that bypassed both —
  // programmatic seeding, legacy localStorage data from before this
  // phase, etc.
  //
  // We only compute a fallback when the field is empty or malformed.
  // A valid (auditor-typed) value is preserved as-is, including
  // grace-period overrides (the typical reason for manual input).
  if (
    payload.payment &&
    (!payload.payment.firstPaymentDate ||
      !ISO_DATE_RE.test(payload.payment.firstPaymentDate))
  ) {
    const computed = computeFirstPaymentDate(
      payload.commencementDate,
      payload.payment.timing,
      payload.payment.frequency,
    );
    if (computed) {
      payload.payment = {
        ...payload.payment,
        firstPaymentDate: computed,
      };
    }
  }

  // Bug C — commencementSpotRate is optional (FX-scoped).
  const spot = toOptionalNumber(
    rest.commencementSpotRate,
    "commencementSpotRate",
  );
  if (spot === undefined) {
    delete (payload as Partial<ContractInput>).commencementSpotRate;
  } else {
    payload.commencementSpotRate = spot;
  }

  if (modifications && modifications.length > 0) {
    payload.modifications = [...modifications].sort((a, b) =>
      a.modificationDate.localeCompare(b.modificationDate),
    ) as ContractInput["modifications"];
  }

  if (historicalRates && historicalRates.length > 0) {
    // Bug C — `rate` may arrive as a string when the auditor typed it
    // into the rate input without a save/round-trip through the
    // resolver. Coerce per row.
    payload.historicalRates = [...historicalRates]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((r, i) => ({
        date: r.date,
        rate: toNumber(r.rate, `historicalRates[${i}].rate`),
      })) as ContractInput["historicalRates"];
  }

  if (paymentDateRates && paymentDateRates.length > 0) {
    payload.paymentDateRates = [...paymentDateRates]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((r, i) => ({
        date: r.date,
        rate: toNumber(r.rate, `paymentDateRates[${i}].rate`),
      })) as ContractInput["paymentDateRates"];
  }

  // Phase 6a.20c — termination pass-through with numeric coercion.
  // Same boundary-coercion rationale as the existing Bug C fix: form
  // numeric inputs emit strings while the auditor types, but the
  // backend's `terminationInputSchema` is strict `z.number()` on every
  // numeric field. Coerce here so the wire payload carries native
  // numbers regardless of which form path produced the slot.
  if (termination !== undefined) {
    const t = termination;
    payload.termination = {
      terminationDate: t.terminationDate,
      terminationPenalty: toOptionalNumber(
        t.terminationPenalty,
        "termination.terminationPenalty",
      ),
      terminationPenaltyCurrency: t.terminationPenaltyCurrency,
      terminationPenaltyDateRate: toOptionalNumber(
        t.terminationPenaltyDateRate,
        "termination.terminationPenaltyDateRate",
      ),
      assetReturnDate: t.assetReturnDate,
      restorationCostsActual: toOptionalNumber(
        t.restorationCostsActual,
        "termination.restorationCostsActual",
      ),
      terminationDateSpotRate: toOptionalNumber(
        t.terminationDateSpotRate,
        "termination.terminationDateSpotRate",
      ),
      description: t.description,
    } as ContractInput["termination"];
  }

  // Phase 6a.20d — index linkage. When the master toggle is OFF (or
  // never enabled), strip the entire family from the payload so the
  // backend never sees stale `indexResets: []` etc. Backend's step-vs-
  // index refine in `contractInputSchema` fires on
  // `indexResets?.length > 0`, so even an empty array is benign, but
  // keeping the wire payload tidy avoids surprises in audit logs.
  //
  // When ON, coerce the three numeric leaves (top-level cap + floor,
  // per-entry increaseRatePercent) to native numbers — same boundary
  // story as the rest of preparePayload.
  if (indexLinked === true) {
    payload.indexLinked = true;
    if (indexType !== undefined) payload.indexType = indexType;
    if (indexResetFrequency !== undefined) {
      payload.indexResetFrequency = indexResetFrequency;
    }
    const cap = toOptionalNumber(indexCapPercent, "indexCapPercent");
    if (cap === undefined) {
      delete (payload as Partial<ContractInput>).indexCapPercent;
    } else {
      payload.indexCapPercent = cap;
    }
    const floor = toOptionalNumber(indexFloorPercent, "indexFloorPercent");
    if (floor === undefined) {
      delete (payload as Partial<ContractInput>).indexFloorPercent;
    } else {
      payload.indexFloorPercent = floor;
    }
    if (Array.isArray(indexResets) && indexResets.length > 0) {
      payload.indexResets = [...indexResets]
        .sort((a, b) => a.resetDate.localeCompare(b.resetDate))
        .map((r, i) => ({
          resetDate: r.resetDate,
          increaseRatePercent: toNumber(
            r.increaseRatePercent,
            `indexResets[${i}].increaseRatePercent`,
          ),
          notes: r.notes,
        })) as ContractInput["indexResets"];
    } else {
      delete (payload as Partial<ContractInput>).indexResets;
    }
  } else {
    delete (payload as Partial<ContractInput>).indexLinked;
    delete (payload as Partial<ContractInput>).indexType;
    delete (payload as Partial<ContractInput>).indexResetFrequency;
    delete (payload as Partial<ContractInput>).indexCapPercent;
    delete (payload as Partial<ContractInput>).indexFloorPercent;
    delete (payload as Partial<ContractInput>).indexResets;
  }

  // Phase 6a.20e — prior year reconciliation. The toggle in
  // PriorReconciliationSection is the source of truth. When OFF, the
  // section's setValue clears all three fields, but we still defensively
  // strip from the payload in case a stale slot survives a UI bypass
  // (e.g. legacy localStorage). When ON, coerce every numeric leaf on
  // priorPeriodBalances and drop empty-string optionals so backend's
  // strict z.number().optional() does not reject `""` mid-typing.
  const hasPriorRecon =
    typeof priorReportingDate === "string" &&
    priorReportingDate !== "" &&
    priorPeriodBalances !== undefined;
  if (hasPriorRecon) {
    payload.priorReportingDate = priorReportingDate;
    const ppbIn = priorPeriodBalances as Record<string, unknown>;
    const ppbOut: Record<string, number | string> = {};

    // PRIOR-RECON-CCY-BASIS — currencyBasis is a string enum, not numeric.
    // Pass through verbatim when present; backend defaults to 'FUNCTIONAL'
    // when omitted (regression-safe). Reject any other value at the
    // boundary so a typo doesn't reach the engine.
    const basisRaw = ppbIn.currencyBasis;
    if (basisRaw === "FUNCTIONAL" || basisRaw === "CONTRACT") {
      ppbOut.currencyBasis = basisRaw;
    }

    // Core balances — required at UI layer. toNumber throws on garbage,
    // matching the Bug C boundary discipline (better to surface a typed
    // error than silently send NaN over the wire).
    ppbOut.rouGrossOpening = toNumber(
      ppbIn.rouGrossOpening,
      "priorPeriodBalances.rouGrossOpening",
    );
    ppbOut.rouAccumulatedDepreciation = toNumber(
      ppbIn.rouAccumulatedDepreciation,
      "priorPeriodBalances.rouAccumulatedDepreciation",
    );
    ppbOut.leaseLiabilityShortTerm = toNumber(
      ppbIn.leaseLiabilityShortTerm,
      "priorPeriodBalances.leaseLiabilityShortTerm",
    );
    ppbOut.leaseLiabilityLongTerm = toNumber(
      ppbIn.leaseLiabilityLongTerm,
      "priorPeriodBalances.leaseLiabilityLongTerm",
    );

    // Optional balance items — strip empty strings + undefined; coerce
    // anything truthy. The form field names ("restoration", "prepaid",
    // "incentiveLiability") map to backend field names ("restorationProvision",
    // "prepaidLeasePayments", "leaseIncentiveLiability").
    const optionalMap: Array<{ form: string; backend: string }> = [
      { form: "restoration", backend: "restorationProvision" },
      { form: "prepaid", backend: "prepaidLeasePayments" },
      { form: "incentiveLiability", backend: "leaseIncentiveLiability" },
      { form: "liabilityContractCcy", backend: "liabilityContractCcy" },
      { form: "priorYearClosingRate", backend: "priorYearClosingRate" },
    ];
    for (const { form: src, backend: dst } of optionalMap) {
      const v = toOptionalNumber(
        ppbIn[src],
        `priorPeriodBalances.${src}`,
      );
      if (v !== undefined) ppbOut[dst] = v;
    }

    payload.priorPeriodBalances =
      ppbOut as unknown as ContractInput["priorPeriodBalances"];

    const tol = toOptionalNumber(
      priorReportingTolerance,
      "priorReportingTolerance",
    );
    if (tol === undefined) {
      delete (payload as Partial<ContractInput>).priorReportingTolerance;
    } else {
      payload.priorReportingTolerance = tol;
    }
  } else {
    delete (payload as Partial<ContractInput>).priorReportingDate;
    delete (payload as Partial<ContractInput>).priorPeriodBalances;
    delete (payload as Partial<ContractInput>).priorReportingTolerance;
  }

  // Additional Measurement Inputs (IFRS 16.27(c)-(d), 16.32). Same
  // delete-when-empty / coerce-at-the-boundary discipline as the FX +
  // index families above: coerce the three numeric fields and drop them
  // when undefined; pass the two booleans through ONLY when true (drop
  // when false/undefined to keep the wire payload tidy — the backend
  // treats a missing boolean as false).
  const rvg = toOptionalNumber(
    residualValueGuarantee,
    "residualValueGuarantee",
  );
  if (rvg === undefined) {
    delete (payload as Partial<ContractInput>).residualValueGuarantee;
  } else {
    payload.residualValueGuarantee = rvg;
  }

  const optionPrice = toOptionalNumber(
    purchaseOptionPrice,
    "purchaseOptionPrice",
  );
  if (optionPrice === undefined) {
    delete (payload as Partial<ContractInput>).purchaseOptionPrice;
  } else {
    payload.purchaseOptionPrice = optionPrice;
  }

  const usefulLife = toOptionalNumber(
    assetUsefulLifeMonths,
    "assetUsefulLifeMonths",
  );
  if (usefulLife === undefined) {
    delete (payload as Partial<ContractInput>).assetUsefulLifeMonths;
  } else {
    payload.assetUsefulLifeMonths = usefulLife;
  }

  if (purchaseOptionReasonablyCertain === true) {
    payload.purchaseOptionReasonablyCertain = true;
  } else {
    delete (payload as Partial<ContractInput>).purchaseOptionReasonablyCertain;
  }

  if (transfersOwnership === true) {
    payload.transfersOwnership = true;
  } else {
    delete (payload as Partial<ContractInput>).transfersOwnership;
  }

  return payload;
}
